import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import nithinPortrait from '../nithin.png';

const heroEase = [0.22, 1, 0.36, 1];

function Hero() {
  const shapeLeftRef = useRef(null);
  const shapeRightRef = useRef(null);
  const portraitRef = useRef(null);
  const heroWordRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });

    if (heroWordRef.current && portraitRef.current) {
      intro
        .fromTo(heroWordRef.current, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.75 })
        .fromTo(portraitRef.current, { y: 26, opacity: 0 }, { y: 0, opacity: 1, duration: 0.82 }, '-=0.44');
    }

    let parallaxFrame = null;
    const onParallax = (event) => {
      if (parallaxFrame) cancelAnimationFrame(parallaxFrame);
      parallaxFrame = requestAnimationFrame(() => {
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const dx = (event.clientX - cx) / cx;
        const dy = (event.clientY - cy) / cy;
        if (portraitRef.current) {
          portraitRef.current.style.setProperty('--px', `${dx * 14}px`);
          portraitRef.current.style.setProperty('--py', `${dy * 10}px`);
        }
        if (shapeLeftRef.current) {
          shapeLeftRef.current.style.setProperty('--bx', `${dx * 22}px`);
          shapeLeftRef.current.style.setProperty('--by', `${dy * 16}px`);
        }
        if (shapeRightRef.current) {
          shapeRightRef.current.style.setProperty('--bx', `${dx * -26}px`);
          shapeRightRef.current.style.setProperty('--by', `${dy * -18}px`);
        }
      });
    };
    if (!window.matchMedia('(hover: none)').matches) {
      window.addEventListener('mousemove', onParallax);
    }


    return () => {
      intro.kill();
      gsap.killTweensOf(shapeLeftRef.current);
      gsap.killTweensOf(shapeRightRef.current);
      gsap.killTweensOf(portraitRef.current);
      window.removeEventListener('mousemove', onParallax);
      if (parallaxFrame) cancelAnimationFrame(parallaxFrame);
    };
  }, []);

  return (
    <section id="home" className="relative overflow-hidden pb-20 pt-28 md:pb-28 md:pt-36">
      <div className="section-wrap relative">
        <h1 className="sr-only">
          Nithin Sai Choudray freelance full stack web developer and website designer in Hyderabad, India.
        </h1>

        <div className="md:hidden">
          <div className="mb-6 flex items-center justify-between">
            <span className="meta-label">Est. 2025</span>
            <span className="meta-label">Portfolio</span>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.7 }}
            transition={{ duration: 0.5, ease: heroEase }}
            className="mx-auto mb-4 w-fit text-center text-[12px] font-medium text-ink/78"
          >
            Nithin Sai Choudray
          </motion.p>

          <div className="relative overflow-hidden rounded-[2rem] border border-ink/12 bg-snow/75 px-4 pb-4 pt-5">
            <div className="pointer-events-none absolute -left-8 top-4 h-20 w-20 rounded-full border border-ink/12 bg-snow/50" />
            <div className="pointer-events-none absolute -right-10 top-10 h-24 w-24 rounded-full border border-ink/12 bg-snow/45" />

            <p className="copy-refined mb-4 text-center text-xs text-ink/70">
              Freelance full stack web developer and website designer crafting premium digital products from Hyderabad.
            </p>

            <div className="relative z-10 mx-auto max-w-[95%]">
              <p className="text-outline text-center font-display text-[13.2vw] font-bold uppercase leading-[0.88] tracking-[-0.02em] max-[360px]:text-[12.4vw]">
                Full
              </p>
              <p className="text-outline -mt-[1.3vw] text-center font-display text-[13.2vw] font-bold uppercase leading-[0.88] tracking-[-0.02em] max-[360px]:text-[12.4vw]">
                Stack
              </p>
              <h2 className="mt-[0.8vw] whitespace-nowrap text-center font-display text-[11.6vw] font-bold uppercase leading-[0.92] tracking-[-0.016em] max-[420px]:text-[11.2vw] max-[360px]:text-[10.6vw]">
                Developer
              </h2>
            </div>

            <div className="relative z-20 mx-auto mt-2 h-[292px] w-[222px]">
              <img
                src={nithinPortrait}
                alt="Portrait of Nithin Sai Choudray"
                className="h-full w-full object-contain grayscale contrast-110"
                loading="eager"
                decoding="async"
                width="882"
                height="1280"
                fetchPriority="high"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-snow/90" />
            </div>

            <div className="mt-4 flex items-center gap-2">
                <a
                  href="#work"
                  className="ui-button-dark inline-flex flex-1 justify-center rounded-full border border-ink bg-coal px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.17em] text-white"
                >
                  View Work
                </a>
                <a
                  href="#contact"
                  className="ui-button-light inline-flex flex-1 justify-center rounded-full border border-ink/30 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.17em] text-ink"
                >
                  Let&apos;s Talk
                </a>
              </div>
          </div>

          <div className="mt-7 grid grid-cols-1 gap-6">
            <div>
              <p className="meta-label mb-3">Core Philosophy</p>
              <p className="copy-refined text-sm text-ink/80">
                I combine design clarity with strong technical execution, translating ambitious ideas into polished,
                performant, and scalable digital experiences.
              </p>
            </div>
            <div>
              <p className="meta-label mb-3">Current Location</p>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink">Hyderabad, India</p>
              <p className="mt-2 text-xs uppercase tracking-[0.22em] text-ink/50">Available for premium projects</p>
            </div>
          </div>
        </div>

        <div className="hidden md:block">
          <div className="mb-8 flex items-center justify-between md:mb-10">
            <span className="meta-label hidden md:block">Est. 2025</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-ink/85">Nithin Sai Choudray</span>
            <span className="meta-label hidden md:block">Portfolio</span>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.7 }}
            transition={{ duration: 0.6, ease: heroEase }}
            className="mx-auto mb-4 w-fit text-center text-sm text-ink/74"
          >
            I am Nithin Sai Choudray, a freelance full stack web developer and website designer building premium
            websites end-to-end.
          </motion.p>

          <div className="relative min-h-[620px] pt-5">
            <div
              ref={shapeLeftRef}
              className="parallax-blob pointer-events-none absolute left-[4%] top-[20%] z-0 h-24 w-24 rounded-full border border-ink/12 bg-snow/45"
              aria-hidden="true"
            />
            <div
              ref={shapeRightRef}
              className="parallax-blob pointer-events-none absolute right-[9%] top-[8%] z-0 h-28 w-28 rounded-full border border-ink/12 bg-snow/40"
              aria-hidden="true"
            />

            <div ref={heroWordRef}>
              <p className="text-outline relative z-10 text-center font-display text-[14vw] font-bold uppercase leading-[0.82] tracking-[-0.03em]">
                Full Stack
              </p>
              <h2 className="relative z-20 -mt-[1.9vw] text-center font-display text-[13.5vw] font-bold uppercase leading-[0.82] tracking-[-0.04em]">
                Developer
              </h2>
            </div>

            <div ref={portraitRef} className="parallax-portrait absolute bottom-0 right-[4%] z-30 h-[560px] w-[380px] lg:right-[6%]">
              <img
                src={nithinPortrait}
                alt="Portrait of Nithin Sai Choudray"
                className="h-full w-full object-contain grayscale contrast-110"
                loading="eager"
                decoding="async"
                width="882"
                height="1280"
                fetchPriority="high"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-paper" />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.75, ease: heroEase }}
            className="relative z-40 mt-7 grid grid-cols-12 gap-6"
          >
            <div className="col-span-4">
              <p className="meta-label mb-3">Core Philosophy</p>
              <p className="copy-refined max-w-md text-sm text-ink/80">
                I combine design clarity with strong technical execution, translating ambitious ideas into polished,
                performant, and scalable digital experiences.
              </p>
            </div>

            <div className="col-span-5 flex items-end justify-center">
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="#work"
                  className="ui-button-dark inline-flex rounded-full border border-ink bg-coal px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.17em] text-white"
                >
                  View Work
                </a>
                <a
                  href="#contact"
                  className="ui-button-light inline-flex rounded-full border border-ink/30 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.17em] text-ink"
                >
                  Let&apos;s Talk
                </a>
              </div>
            </div>

            <div className="col-span-3 text-right">
              <p className="meta-label mb-3">Current Location</p>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink">Hyderabad, India</p>
              <p className="mt-2 text-xs uppercase tracking-[0.22em] text-ink/50">Available for premium projects</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <span className="absolute -left-28 top-[20%] font-display text-[30vw] font-bold uppercase leading-none tracking-[-0.06em] text-ink/[0.03]">
          N
        </span>
        <span className="absolute -right-16 bottom-[12%] font-display text-[28vw] font-bold uppercase leading-none tracking-[-0.06em] text-ink/[0.03]">
          D
        </span>
      </div>
    </section>
  );
}

export default Hero;
